import { KB_MASK_ELEMENTS } from 'shared/constants/urls';
import React, { useMemo } from 'react';
import { Alert, Button, Checkbox, Col, Form, Input, InputNumber, Row } from 'antd';
import DrawerForm from 'components/DrawerForm';
import { PlusOutlined } from '@ant-design/icons';
import { gql } from '@apollo/client';
import { useMutation } from 'hooks/apollo';

import FormLabel from 'components/FormLabel';
import messages from '../messages';
import HsrRule from './HsrRule';

const CREATE_HEATMAP = gql`
  mutation($websiteId: ID!, $input: MatomoSessionRecordingInput!) {
    createMatomoSessionRecording(websiteId: $websiteId, input: $input)
  }
`;

const UPDATE_HEATMAP = gql`
  mutation($websiteId: ID!, $matomoHsrId: Int!, $input: MatomoSessionRecordingInput!) {
    updateMatomoSessionRecording(websiteId: $websiteId, matomoHsrId: $matomoHsrId, input: $input)
  }
`;

const REMOVE_HEATMAP = gql`
  mutation($websiteId: ID!, $matomoHsrId: Int!) {
    deleteMatomoSessionRecording(websiteId: $websiteId, matomoHsrId: $matomoHsrId)
  }
`;

const defaultValues = {
  sampleRate: 10,
  rules: [{ attribute: 'url', type: 'any', inverted: false }],
  minSessionTime: 0,
};

function getInitialValues(sessionRecording) {
  return {
    ...defaultValues,
    ...sessionRecording,
    rules: (sessionRecording.rules || defaultValues.rules)?.map(rule => ({
      ...rule,
      type: `${rule.inverted ? 'not ' : ''}${rule.type}`,
    })),
  };
}

function getInputValues(values) {
  return {
    ...values,
    rules: values.rules.map(rule => ({
      ...rule,
      type: rule.type.indexOf('not ') === 0 ? rule.type.substring(4) : rule.type,
      inverted: rule.type.indexOf('not ') === 0,
    })),
  };
}

const EditSessionRecording = ({ sessionRecording, open, onCancel, onDone, websiteId }) => {
  const [createSessionRecording] = useMutation(CREATE_HEATMAP);
  const [updateWebsite] = useMutation(UPDATE_HEATMAP);
  const [removeSessionRecording] = useMutation(REMOVE_HEATMAP);

  const onSubmit = (values) => {
    const input = getInputValues(values);
    if (sessionRecording?.id) {
      return updateWebsite({ variables: { websiteId, matomoHsrId: sessionRecording.matomoHsrId, input } });
    }
    return createSessionRecording({ variables: { websiteId, input } });
  };

  const onRemove = async () => {
    await removeSessionRecording({ variables: { websiteId, matomoHsrId: sessionRecording.matomoHsrId } });
    onDone();
  };

  const initialValues = useMemo(() => getInitialValues(sessionRecording), [sessionRecording]);

  return (
    <DrawerForm
      open={open}
      onSubmit={onSubmit}
      onRemove={sessionRecording?.id && onRemove}
      onCancel={onCancel}
      onDone={onDone}
      title="Edit SessionRecording"
      initialValues={initialValues}
      formReset={sessionRecording}
    >
      <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Required' }, { max: 50, message: 'Max 50 characters' }]}>
        <Input placeholder="Name" />
      </Form.Item>

      <Form.Item label={<FormLabel label={messages.targetPageRules} description={messages.targetPageRulesDescription} />}>
        <Form.List name="rules">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <HsrRule key={field.key} field={field} remove={remove} isSessionRecording />
              ))}
              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                Add rule
              </Button>
            </>
          )}
        </Form.List>
      </Form.Item>
      <Row gutter={[16, 16]}>

        <Col span={12}>
          <Form.Item name="sampleRate" label={<FormLabel label={messages.sampleRate} description={messages.sampleRateDescription} />} rules={[{ required: true, message: 'Required' }]}>
            <InputNumber min={0} max={100} addonAfter="%" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="minSessionTime" label={<FormLabel label={messages.minSessionTime} description={messages.minSessionTimeDescription} />}>
            <InputNumber min={0} addonAfter="seconds" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="requiresActivity" valuePropName="checked">
        <Checkbox>
          <FormLabel label={messages.requiresActivity} description={messages.requiresActivityDescription} />
        </Checkbox>
      </Form.Item>
      <Form.Item name="captureKeystrokes" valuePropName="checked">
        <Checkbox>
          <FormLabel label={messages.captureKeystrokes} description={messages.captureKeystrokesDescription} />
        </Checkbox>
      </Form.Item>

      <Alert
        message={(
          <>
            Please note a Session Recording may record personal or sensitive information. If you wish to hide certain content on your website or app, you may tag this content by adding a
            {' '}
            <b>data-matomo-mask</b>
            {' '}
            attribute to the HTML markup.
            <br />
            <a href={KB_MASK_ELEMENTS} target="_blank">Learn more</a>
          </>
        )}
        type="info"
        showIcon
      />
    </DrawerForm>
  );
};

export default EditSessionRecording;
